import { useEffect, useState, useRef, useMemo } from "react";
import { BRAND } from "@/config/brand";

const C = BRAND.colors;

// Promotional "sale" card — a full-bleed marketing panel that rotates in
// between the AI twin portraits at slots 2, 4, 6, 8, and 10 (1-indexed).
const SALE_SLIDE = {
  type: "sale",
  src: "/twins/sale-1.png",
  job: "Let's build your AI twin",
  headline: "Think you have a nice website? Meet the AI twin.",
};

// Ordered fallback list — used if manifest.json hasn't been generated yet.
// Every id 01-20 corresponds to a distinct business owner archetype.
const FALLBACK_TWINS = [
  { src: "/twins/twin-01.jpg", job: "Restaurant owner" },
  { src: "/twins/twin-02.jpg", job: "Auto shop owner" },
  { src: "/twins/twin-03.jpg", job: "Pharmacist" },
  { src: "/twins/twin-04.jpg", job: "General contractor" },
  { src: "/twins/twin-05.jpg", job: "IT consultant" },
  { src: "/twins/twin-06.jpg", job: "Barbershop owner" },
  { src: "/twins/twin-07.jpg", job: "Salon owner" },
  { src: "/twins/twin-08.jpg", job: "Florist" },
  { src: "/twins/twin-09.jpg", job: "Medical clinic owner" },
  { src: "/twins/twin-10.jpg", job: "Ranch owner" },
  { src: "/twins/twin-11.jpg", job: "Real-estate agent" },
  { src: "/twins/twin-12.jpg", job: "Tutor / educator" },
  { src: "/twins/twin-13.jpg", job: "Bakery owner" },
  { src: "/twins/twin-14.jpg", job: "Fashion boutique" },
  { src: "/twins/twin-15.jpg", job: "Plumber" },
  { src: "/twins/twin-16.jpg", job: "Gym owner / trainer" },
  { src: "/twins/twin-17.jpg", job: "Yoga studio owner" },
  { src: "/twins/twin-18.jpg", job: "Food-truck owner" },
  { src: "/twins/twin-19.jpg", job: "Accountant / CPA" },
  { src: "/twins/twin-20.jpg", job: "Attorney" },
];

// Interleave sale slide at positions 2, 4, 6, 8, 10 (1-indexed → 0-indexed 1,3,5,7,9).
// Final sequence: t1, SALE, t2, SALE, t3, SALE, t4, SALE, t5, SALE, t6, t7, …, t20.
function withSaleInterleaved(twins) {
  const tagged = twins.map((t) => ({ ...t, type: "twin" }));
  const out = [];
  for (let i = 0; i < tagged.length; i++) {
    out.push(tagged[i]);
    if (i < 5) out.push(SALE_SLIDE);
  }
  return out;
}

// Rotates through all available AI-twin portraits + interleaved sale card,
// cross-fading every 4.5s.
export default function AITwinSlideshow() {
  const [twins, setTwins] = useState(FALLBACK_TWINS);
  const [idx, setIdx] = useState(0);
  const prevIdxRef = useRef(0);

  const slides = useMemo(() => withSaleInterleaved(twins), [twins]);

  // Load manifest to know which images actually made it to disk.
  useEffect(() => {
    let cancelled = false;
    fetch("/twins/manifest.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data || !Array.isArray(data) || data.length === 0) return;
        setTwins(data);
      })
      .catch(() => { /* fallback list already loaded */ });
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      prevIdxRef.current = idx;
      setIdx((i) => (i + 1) % slides.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [idx, slides.length]);

  // Preload the next 2 for smooth cross-fade
  useEffect(() => {
    const next = [(idx + 1) % slides.length, (idx + 2) % slides.length];
    next.forEach((i) => {
      const img = new Image();
      img.src = slides[i].src;
    });
  }, [idx, slides]);

  const current = slides[idx];
  const isSale = current.type === "sale";

  return (
    <div
      className="relative rounded-3xl overflow-hidden border aspect-[16/10]"
      style={{
        borderColor: `${C.blueBright}44`,
        boxShadow: `0 80px 200px -50px ${C.blueBright}66, 0 0 0 1px ${C.line}`,
        background: C.ink,
      }}
      data-testid="ai-twin-slideshow"
    >
      {/* Slides — cross-fade. Aug 2026 mobile-perf fix: only render the current
          slide + immediate neighbours in the DOM so the 21×~700KB twin images
          don't all race for bandwidth before FCP fires. Slide 0 gets high
          fetchpriority so it's the LCP candidate; the rest lazy-load. */}
      {slides.map((s, i) => {
        // Render only slides within ±1 of the active index (plus slide 0 as
        // the LCP anchor). Everything else is a lightweight <div> placeholder.
        const isNear = i === 0 || Math.abs(i - idx) <= 1 || (idx === slides.length - 1 && i === 0);
        if (!isNear) {
          return <div key={`ph-${i}`} className="absolute inset-0" style={{ opacity: 0 }} aria-hidden />;
        }
        return (
          <img
            key={`${s.type}-${i}-${s.src}`}
            src={s.src}
            alt={s.type === "sale"
              ? "Think you have a nice website? Do you have a bot running 24/7 getting you leads? Let's build your AI twin today. Powered by PlugitAI · Nariai."
              : `Business owner and their glowing AI twin — ${s.job}`}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${s.type === "sale" ? "object-contain bg-[#04080F]" : "object-cover"}`}
            style={{ opacity: i === idx ? 1 : 0 }}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "low"}
            decoding={i === 0 ? "sync" : "async"}
            sizes="(max-width: 900px) 100vw, 640px"
            width={640}
            height={400}
            data-testid={`ai-twin-slide-${i}`}
          />
        );
      })}

      {/* Bottom fade — softened over sale card so gold text stays readable */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-28"
        style={{ background: `linear-gradient(to top, ${C.ink}${isSale ? "88" : "ee"}, transparent)` }}
      />

      {/* Top-left status pill */}
      <div
        className="absolute top-4 left-4 md:top-6 md:left-6 flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] z-10"
        style={{
          background: "rgba(10,15,30,0.72)",
          color: isSale ? "#FDE68A" : C.greenGlow,
          border: `1px solid ${isSale ? "#FDE68A66" : `${C.greenGlow}66`}`,
          backdropFilter: "blur(10px)",
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: isSale ? "#FDE68A" : C.greenGlow, boxShadow: `0 0 10px ${isSale ? "#FDE68A" : C.greenGlow}` }} />
        {isSale ? "Limited · lock in $299.99" : "AI twin · online 24/7"}
      </div>

      {/* Bottom-left job label with slide counter */}
      <div
        className="absolute bottom-4 left-4 md:bottom-6 md:left-6 flex flex-col gap-2 z-10"
        data-testid="ai-twin-current-job"
      >
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] self-start"
          style={{
            background: "rgba(10,15,30,0.75)",
            color: "white",
            border: `1px solid ${isSale ? "#FDE68A88" : `${C.blueBright}66`}`,
            backdropFilter: "blur(10px)",
          }}
        >
          {isSale ? (
            <>
              <span style={{ color: "#FDE68A" }}>Offer</span>
              <span>{current.job}</span>
            </>
          ) : (
            <>
              <span style={{ color: C.blueGlow }}>Meet</span>
              <span>{current.job}</span>
            </>
          )}
        </div>
        <div className="text-[9px] uppercase tracking-[0.22em] pl-2" style={{ color: C.textFade }}>
          {String(idx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")} · {isSale ? "Special offer" : "Real business owners"}
        </div>
      </div>

      {/* Progress dots */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6 flex gap-1 z-10" data-testid="ai-twin-progress">
        {slides.map((s, i) => (
          <span
            key={i}
            className="rounded-full transition-all"
            style={{
              background: i === idx
                ? (s.type === "sale" ? "#FDE68A" : C.greenGlow)
                : `${C.textFade}66`,
              boxShadow: i === idx
                ? `0 0 8px ${s.type === "sale" ? "#FDE68A" : C.greenGlow}`
                : "none",
              width: i === idx ? 18 : 6,
              height: 6,
            }}
          />
        ))}
      </div>
    </div>
  );
}
